import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Field, change, touch } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { loadLabels } from '../../../../action/job/load-labels';
import { jobLabelsSelector, jobSelector } from '../../../../reducers/job-selectors';

import { CUSTOM_LABEL_SETTINGS } from './transition/custom-hqc-settings';
import { ResponsiveTransition } from './transition/responsive-transition';

import { AUTOMATED_LABEL_KEYS } from '../../../../util/automated-label-keys';
import { JobApplicantLabel } from '../../../../widgets/job-applicant-label';
import Message from '../../../../widgets/legacy-message';
import { LoadingMessage } from '../../../../widgets/loading-message';

type InputProps = ReduxFormFieldProps<Array<HqcSetting>> & {
  jobId: number;
};

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  job: jobSelector(state, ownProps.jobId),
  labels: jobLabelsSelector(state, ownProps.jobId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadLabels: () => dispatch(loadLabels(ownProps.jobId)),
    setLabelSettings: (form: string, labels: Array<AutomatedLabel> | null) => {
      if (labels) {
        dispatch(touch(form, 'JobLabelSettings'));
        dispatch(
          change(
            form,
            'JobLabelSettings',
            labels.filter((l) => l.hasCustomSettings),
          ),
        );
      }
    },
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

const mergeProps = (
  stateProps: StateProps,
  dispatchProps: DispatchProps,
  ownProps: InputProps,
) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  setLabelSettings: () =>
    dispatchProps.actions.setLabelSettings(ownProps.meta.form, stateProps.labels),
});

type Props = ReturnType<typeof mergeProps>;

type State = {
  responsiveDialogOpen: boolean;
};

class HighQualityCandidateFormComponent extends React.PureComponent<Props, State> {
  state: State = {
    responsiveDialogOpen: false,
  };

  componentDidMount() {
    this.props.actions.loadLabels();
  }

  componentDidUpdate(prevProps: Props) {
    this.props.actions.loadLabels();
    if (!prevProps.labels && this.props.labels) {
      this.props.setLabelSettings();
    }
  }

  cancel = (name: string) => async () => {
    this.setState({
      responsiveDialogOpen: false,
    });
    await this.updateLabel(name, false);
  };

  okay = (name: string) => async () => {
    this.setState({
      responsiveDialogOpen: false,
    });
    await this.updateLabel(name, true);
  };

  hasSetting = (automatedLabelKey: string) =>
    (this.props.input.value || []).find((l) => l.automatedLabelKey === automatedLabelKey) !==
    undefined;

  handleLabelChange = (name: string) => async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (name === AUTOMATED_LABEL_KEYS.Responsive && event.target.checked) {
      this.setState({
        responsiveDialogOpen: true,
      });
      return;
    }
    await this.updateLabel(name, event.target.checked);
  };

  updateLabel = async (automatedLabelKey: string, enabled: boolean) => {
    const { job } = this.props;
    if (!job) {
      return;
    }
    const newSettings = this.props.input.value.slice();
    if (enabled) {
      newSettings.push({
        automatedLabelKey,
        jobId: job.job_id,
      });
    } else {
      const index = newSettings.findIndex((s) => s.automatedLabelKey === automatedLabelKey);
      newSettings.splice(index, 1);
    }

    this.props.input.onChange(newSettings);
  };

  renderLabelList = (title: string, labels: Array<AutomatedLabel>) => (
    <div>
      <Typography variant="subtitle2" className="u-verticalAlign-middle">
        {title}
      </Typography>
      <Grid container spacing={2}>
        <Grid item container xs>
          <FormGroup>
            {labels.map((label) => (
              <React.Fragment key={label.automatedLabelKey}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={this.hasSetting(label.automatedLabelKey)}
                      onChange={this.handleLabelChange(label.automatedLabelKey)}
                      color="primary"
                    />
                  }
                  label={<JobApplicantLabel label={label} />}
                />
                {label.hasCustomSettings && (
                  <Collapse in={this.hasSetting(label.automatedLabelKey)}>
                    {this.hasSetting(label.automatedLabelKey) && (
                      // See applicant-requirements-tab for the initial value here
                      <Field
                        name="JobLabelSettings"
                        component={CUSTOM_LABEL_SETTINGS[label.automatedLabelKey]}
                      />
                    )}
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </FormGroup>
        </Grid>
      </Grid>
    </div>
  );

  render() {
    const { labels, job } = this.props;
    const { responsiveDialogOpen } = this.state;

    if (!(labels && job)) {
      return <LoadingMessage />;
    }

    const labelsUserCanChange = labels.filter((label) => !label.isAutoManaged);
    const positiveLabels = labelsUserCanChange.filter((l) => l.isPositive);
    const negativeLabels = labelsUserCanChange.filter((l) => !l.isPositive);

    return (
      <div>
        <Grid container spacing={2}>
          <Grid item container xs>
            {this.renderLabelList('An Applicant Must Have', positiveLabels)}
          </Grid>
          <Grid item container xs>
            {this.renderLabelList('An Applicant Must Not Have', negativeLabels)}
          </Grid>
        </Grid>
        {responsiveDialogOpen && (
          <ResponsiveTransition
            job={job}
            org={job.org}
            onCancel={this.cancel('Responsive')}
            onConfirmation={this.okay('Responsive')}
          />
        )}
        {this.props.meta.dirty && (
          <div className="u-margin-top-md">
            <Message
              message="NOTE: This will only apply to future applicants."
              canBeDismissed={false}
              variant="warning"
            />
          </div>
        )}
      </div>
    );
  }
}

export const HighQualityCandidateForm = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(HighQualityCandidateFormComponent);
